import { useCallback, useEffect, useRef } from "react";
import type {
  PurchaseFlow,
  PurchaseFunnelStep,
  PurchaseSheetScreen,
} from "./purchase-flow";

/** One visible opening, even when a corrected catalog replaces its content. */
export function useSheetFunnelOpening() {
  return useRef({
    opened: false,
    chosen: false,
    closed: false,
    mounted: false,
    generation: 0,
  }).current;
}

export type SheetFunnelOpening = ReturnType<typeof useSheetFunnelOpening>;

/** The host observes one opening, one selection and one real departure.
 *  Link in Bio's sheet and the app's (apps/mobile PurchaseSheet) alike. */
export function useSheetFunnel(
  engine: Pick<PurchaseFlow, "onFunnelStep" | "preview">,
  screen: PurchaseSheetScreen,
  selection: Extract<PurchaseFunnelStep, { step: "items_choose" }>,
  opening?: SheetFunnelOpening,
) {
  const latest = useRef({ engine, screen, selection });
  latest.current = { engine, screen, selection };
  const localOpening = useSheetFunnelOpening();
  const state = opening ?? localOpening;
  const open = useCallback(() => {
    const { engine } = latest.current;
    if (engine.preview || !engine.onFunnelStep || state.opened) return;
    state.opened = true;
    engine.onFunnelStep({ step: "sheet_open" });
  }, [state]);
  const step = useCallback(
    (step: PurchaseFunnelStep) => {
      if (latest.current.engine.preview) return;
      // A contact slot can already show a code in its mount layout effect.
      open();
      latest.current.engine.onFunnelStep?.(step);
    },
    [open],
  );
  const close = useCallback(() => {
    if (state.closed) return;
    state.closed = true;
    step({ step: "sheet_close", lastScreen: latest.current.screen });
  }, [state, step]);
  useEffect(() => {
    state.mounted = true;
    const generation = ++state.generation;
    open();
    const restore = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      state.opened = false;
      state.chosen = false;
      state.closed = false;
      open();
    };
    // A page (the web, app-web) can go without unmounting the sheet; a
    // native app has no page, only the unmount below.
    const page =
      typeof window !== "undefined" &&
      typeof window.addEventListener === "function"
        ? window
        : null;
    page?.addEventListener("pagehide", close);
    page?.addEventListener("pageshow", restore);
    return () => {
      state.mounted = false;
      page?.removeEventListener("pagehide", close);
      page?.removeEventListener("pageshow", restore);
      // StrictMode immediately sets up the same opening again. A real
      // unmount stays gone; a document departure reports synchronously above.
      queueMicrotask(() => {
        if (!state.mounted && state.generation === generation) close();
      });
    };
  }, [state, open, close]);
  const choose = useCallback(() => {
    if (state.chosen) return;
    state.chosen = true;
    step(latest.current.selection);
  }, [state, step]);
  return { step, close, choose };
}
